/**
 * Lightweight analytics beacon for HireBroker web frontend.
 * Sends pageviews & events to admin API: POST /api/analytics/track
 */

const API_BASE = 'https://app.hirebroker.co/api';
const SESSION_KEY = 'hb_web_sid';
const QUEUE_KEY = 'hb_web_aq';
const ATTR_KEY = 'hb_web_utm';
const REF_KEY = 'hb_web_ref';

function uuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID().replace(/-/g, '');
  }
  return `s${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
}

export function getSessionId() {
  try {
    let sid = localStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = uuid().slice(0, 32);
      localStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch {
    return uuid().slice(0, 32);
  }
}

function clean(value) {
  if (value == null) return undefined;
  const text = String(value).trim();
  return text ? text.slice(0, 180) : undefined;
}

function readUrlAttribution() {
  try {
    const p = new URLSearchParams(window.location.search);
    return {
      utm_source: clean(p.get('utm_source')),
      utm_medium: clean(p.get('utm_medium')),
      utm_campaign: clean(p.get('utm_campaign')),
      utm_term: clean(p.get('utm_term') || p.get('keyword')),
      utm_content: clean(p.get('utm_content')),
      search_query: clean(p.get('q') || p.get('query') || p.get('s') || p.get('text')),
    };
  } catch {
    return {};
  }
}

function isHireBrokerHost(host) {
  const h = String(host || '').replace(/^www\./i, '').toLowerCase();
  return h === 'hirebroker.co' || h.endsWith('.hirebroker.co');
}

function firstReferrer() {
  const current = typeof document !== 'undefined' ? document.referrer : '';
  try {
    const stored = sessionStorage.getItem(REF_KEY);
    if (stored !== null) {
      return stored || undefined;
    }
    let host = '';
    try {
      host = current ? new URL(current).hostname : '';
    } catch {
      host = '';
    }
    const value = current && !isHireBrokerHost(host) ? current : '';
    sessionStorage.setItem(REF_KEY, value);
    return value || undefined;
  } catch {
    return current || undefined;
  }
}

function captureAttribution() {
  const current = readUrlAttribution();
  try {
    const stored = JSON.parse(sessionStorage.getItem(ATTR_KEY) || '{}') || {};
    const firstTouch = {
      utm_source: stored.utm_source || current.utm_source,
      utm_medium: stored.utm_medium || current.utm_medium,
      utm_campaign: stored.utm_campaign || current.utm_campaign,
      utm_term: stored.utm_term || current.utm_term,
      utm_content: stored.utm_content || current.utm_content,
    };
    sessionStorage.setItem(ATTR_KEY, JSON.stringify(firstTouch));
    return { ...firstTouch, search_query: current.search_query };
  } catch {
    return current;
  }
}

function getCustomerId() {
  try {
    // Prefer redux-persisted auth if present
    const raw = localStorage.getItem('persist:root') || localStorage.getItem('auth');
    if (!raw) return undefined;
    const parsed = JSON.parse(raw);
    const auth = typeof parsed?.auth === 'string' ? JSON.parse(parsed.auth) : parsed?.auth || parsed;
    const id = auth?.customer_id || auth?.user?.id || auth?.user?.customer_id;
    return id ? Number(id) : undefined;
  } catch {
    return undefined;
  }
}

async function postEvent(payload) {
  try {
    await fetch(`${API_BASE}/analytics/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
      mode: 'cors',
    });
  } catch {
    // best-effort; queue for later
    try {
      const q = JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]');
      q.push(payload);
      localStorage.setItem(QUEUE_KEY, JSON.stringify(q.slice(-40)));
    } catch {
      /* ignore */
    }
  }
}

/**
 * Track a page view (call on route change).
 */
export function trackPageView(pathname, pageTitle) {
  if (typeof window === 'undefined') return;
  const attr = captureAttribution();
  postEvent({
    session_id: getSessionId(),
    event_type: 'pageview',
    path: pathname || window.location.pathname || '/',
    page_title: pageTitle || document.title || undefined,
    referrer: firstReferrer() || document.referrer || undefined,
    customer_id: getCustomerId(),
    ...attr,
  });
}

/**
 * Track a named action (search, contact, signup, etc.).
 * @param {string} eventName e.g. 'property_view', 'contact_agent', 'search'
 * @param {object} meta optional metadata
 * @param {string} eventType pageview|click|search|lead|signup|login|custom
 */
export function trackEvent(eventName, meta = {}, eventType = 'custom') {
  if (typeof window === 'undefined') return;
  const attr = captureAttribution();
  const searchQuery = clean(meta.search_query || meta.query || meta.q || attr.search_query);
  postEvent({
    session_id: getSessionId(),
    event_type: eventType,
    event_name: eventName,
    path: window.location.pathname || '/',
    page_title: document.title || undefined,
    referrer: firstReferrer() || document.referrer || undefined,
    customer_id: getCustomerId(),
    search_query: searchQuery,
    meta,
    ...attr,
    ...(searchQuery ? { search_query: searchQuery } : {}),
  });
}

/** Flush any offline queue (call once on app load). */
export function flushAnalyticsQueue() {
  try {
    const q = JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]');
    if (!Array.isArray(q) || !q.length) return;
    localStorage.removeItem(QUEUE_KEY);
    q.slice(0, 25).forEach((payload) => postEvent(payload));
  } catch {
    /* ignore */
  }
}
